<template>
  <main id="mainContent" class="lookup">
    <div class="app-main-content">
      <h1>{{ screenData.title }}</h1>
      <p>{{ screenData.description }}</p>

      <form @submit.prevent="handleNextStep">
        <div class="app-input-row">
          <div>
            <div class="select-lookup">
              <input
                type="radio"
                class="radio-input"
                id="lookupConfirmationCode"
                v-model="appointmentType"
                name="lookupType"
                value="confirmationNumber"
              />
              <label for="lookupConfirmationCode">Look up by confirmation code</label>
            </div>
            <ValidationForm ref="confirmationCode" class="validateForm">  
              <Field name="confirmationCode" v-slot="{ field, errors }" rules="alpha_num|max:20">
                <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0  }">
                  <input type="text" id="confirmationCode" v-model="myCode" class="lookup-type"
                    :disabled="appointmentType != 'confirmationNumber'"
                    :readonly="appointmentType != 'confirmationNumber'"
                    v-bind="field"/>
                  <p>{{ errors[0] }}</p>
                </div>
              </Field>
            </ValidationForm>
          </div>
          <span class="or"></span>
          <div>
            <div class="select-lookup">
              <input
                type="radio"
                class="radio-input"
                id="confirmationEmailAddress"
                v-model="appointmentType"
                name="lookupType"
                value="emailId"
              />
              <label for="confirmationEmailAddress">Look up by email address</label>
            </div>
            <ValidationForm ref="emailForm" class="validateForm">   
              <Field name="email" v-slot="{ field, errors }" rules="email">
                <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 || invalidEmailChars || emailCharacterLimitExceeded }">
                  <input type="text" id="emailAddress" v-model="myEmail" class="lookup-type"
                    :disabled="appointmentType != 'emailId'"
                    :readonly="appointmentType != 'emailId'"
                    v-bind="field"/>
                  <p v-if="invalidEmailChars">Email address contains invalid characters.</p>
                  <p v-else-if="emailCharacterLimitExceeded">Email address is limited to 80 characters.</p>
                  <p v-else>{{ errors[0] }}</p>
                </div>
              </Field>
            </ValidationForm>
          </div>
          <span class="or"></span>
          <div>
            <div class="select-lookup">
              <input
                type="radio"
                class="radio-input"
                id="confirmationPhoneNumber"
                v-model="appointmentType"
                name="lookupType"
                value="phoneNumber"
              />
              <label for="confirmationPhoneNumber">Look up by phone number (Numbers only)</label>
            </div>
            <ValidationForm ref="phoneForm" class="validateForm">   
              <Field name="phoneNumber" v-slot="{ field, errors }" rules="numeric">
                <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 || invalidPhoneNumChars || phoneCharacterLimitExceeded }">
                  <input type="text" id="phoneNumber" v-model="myPhone" class="lookup-type"
                    :disabled="appointmentType != 'phoneNumber'"
                    :readonly="appointmentType != 'phoneNumber'"
                    v-bind="field"/>
                  <p v-if="invalidPhoneNumChars">Phone number contains invalid characters.</p>
                  <p v-else-if="phoneCharacterLimitExceeded">Phone number is limited to 10 digits.</p>
                  <p v-else>{{ errors[0] }}</p>
                </div>
              </Field>
            </ValidationForm>
          </div>
        </div>
        <p hidden ref="noLookupInfoElem" class="app-invalid-msg">
          A confirmation code, email or phone is required.
        </p>

        <BaseButtonGroup>
          <template v-if="defaultButtons">
            <button type="button" @click="router.push({ name: PAGES.home })">{{ language.btnPrev }}</button>
            <button type="submit" class="app-btn-primary" @click.prevent="handleNextStep">{{ language.btnNext }}</button>
          </template>
          <template v-else>
            <button type="button" @click="router.push({ name: PAGES.selectType })">{{ language.btnMakeRes }}</button>
            <button type="submit" class="app-btn-primary" @click.prevent="handleNextStep">{{ language.btnReTry }}</button>
          </template>
        </BaseButtonGroup>
      </form>
    </div>
  </main>
</template>


<script setup>
import { ref, computed, onMounted, watch} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useForm, Field, Form as ValidationForm, defineRule } from 'vee-validate';
import {  numeric, max as maxRule, email as emailRule, alpha_num } from '@vee-validate/rules';
import appConfig from '@/app.config';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import { RESERVATION_FIND, RESERVATION_FIND_CONTACT } from '@/store/_commons';
import { useHead } from '@unhead/vue'; 
import { usePhoneFormatter } from '@/composables/usePhoneFormatter'; 

// Use unhead to manage the page meta
useHead({
  title: `Reservation lookup | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'Look up an existing reservation with your confirmation code or email address.'
    }
  ]
})

defineRule('email', (value, _, ctx) => {
  const valid = emailRule(value);
  if (!valid) {
    return `Please enter a valid ${ctx.field}.`
  }
  return true;
});

defineRule('length', (value, [limit]) => {
  if (!value || !value.length) {
    return true; // Skip validation if the field is empty (unless 'required' is also used)
  }
  if (value.length !== limit) {
    return `Phone number must have ${limit} digits.`;
  }
  return true;
});

defineRule('alpha_num', (value) => {
  if (!alpha_num(value)) { 
    return 'Only alphanumeric characters are allowed.';
  }
  return true;
});

defineRule('max', (value, [limit], ctx) => {
  const isValid = maxRule(value, [limit]);
  if (!isValid) {
    return `${ctx.field} is limited to ${limit} characters.`
  }
  return true;
});

defineRule('numeric', numeric); 

defineOptions({
  name: 'Confirmation',
});

// Vuex store
const store = useStore();
const router = useRouter();
const route = useRoute();
const { validate } = useForm();

// Reactive state vars
const appointmentType = ref('confirmationNumber');
const myCode = ref('');
const myEmail = ref('');
const myPhone = ref('');
const screenData = ref({});
const defaultButtons = ref(true);
const confirmationNumber = ref('');
const stepNum = ref(6);


const confirmationCode = ref(null);
const emailForm = ref(null);
const phoneForm = ref(null);

// Computed properties
const language = computed(() => store.state.user.LANG);
const PAGES = computed(() => store.state.user.PAGES);
const STEPS = computed(() => store.state.user.STEPS);

const setStoreProperty = (payload) => store.dispatch('user/setStoreProperty', payload);
const noLookupInfoElem = ref(null);

const invalidEmailChars = computed(() => {
  const pattern = /^[a-zA-Z0-9@._-]*$/;
  return !pattern.test(myEmail.value);
});

const emailCharacterLimitExceeded = computed(() => myEmail.value.length > 80);

const invalidPhoneNumChars = computed(() => {
  if (myPhone.value.length === 0) {
    return false;
  } 
  const pattern = /^\d+$/;
  return !pattern.test(myPhone.value);
});

const phoneCharacterLimitExceeded = computed(() => myPhone.value.length > 10);

// Composable for phone 
const { formatPhoneForRims } = usePhoneFormatter();

watch(appointmentType, () => {
  myCode.value = '';
  myEmail.value = '';
  myPhone.value = '';
  
  // Explicitly call resetForm() on the forms that are NOT the new type
  if (confirmationCode.value) {
    confirmationCode.value.resetForm();
  }
  if (emailForm.value) {
    emailForm.value.resetForm();
  }
  if (phoneForm.value) {
    phoneForm.value.resetForm();
  }
});

// Lifecycle hook
onMounted(() => {
  screenData.value = STEPS.value[stepNum.value];

  // Logic from created()
  confirmationNumber.value = route.query.confirmationNumber;
  if (confirmationNumber.value != undefined && confirmationNumber.value != null) {
    localStorage.setItem('confirmationNumber', confirmationNumber.value);
    localStorage.setItem('onlineToken', route.query.onlineToken);
    store.state.user.onlineToken = route.query.onlineToken;
    getAutoLookUpRes();
  }

  // Logic from mounted()
  if(route.query.ConfirmationNumber != undefined ) {
    confirmationNumber.value = route.query.ConfirmationNumber;
    myCode.value = route.query.ConfirmationNumber;
    handleNextStep();
  }
});

const handleNextStep = async () => {
  const hasLookupInfo = myCode.value.length > 0 || myEmail.value.length > 0 || myPhone.value.length > 0;

  // Handle missing lookup info
  if (noLookupInfoElem.value) {
    noLookupInfoElem.value.hidden = hasLookupInfo;
    if (!hasLookupInfo) return;
  }

  // Validation each lookup type
  const validators = {
    confirmationNumber: () => confirmationCode.value.validate(),
    emailId: () => emailForm.value.validate(),
    phone: () => phoneForm.value.validate(),
  };

  // Check validations 
  if (validators[appointmentType.value]) {
    const { valid } = await validators[appointmentType.value]();
    if (!valid) return;
  }

  const hasInvalidChars = 
    phoneCharacterLimitExceeded.value || emailCharacterLimitExceeded.value || 
    invalidEmailChars.value || invalidPhoneNumChars.value;

  if (hasInvalidChars) return;

  const { valid } = await validate();
  if (!valid) return;

  // lookup payload
  const info = {
    appointmentNumber: myCode.value.toUpperCase(),
    emailAddress: myEmail.value,
    phoneNumber: formatPhoneForRims(myPhone.value),
  };

  setStoreProperty({ property: 'resLookup', value: info });

  // Fetch reservations
  let reservationsFound = [];
  try {
    const action = window.config.USEMFA ? RESERVATION_FIND_CONTACT : RESERVATION_FIND;
    reservationsFound = await store.dispatch(action, info);
  } catch (error) {
    return;
  }

  if (!reservationsFound || reservationsFound.length === 0) return;

  store.commit('user/setReservationsFound', reservationsFound);

  // Navigate to next page
  const nextPage = window.config.USEMFA ? PAGES.value.verifyRes : PAGES.value.updateRes;

  router.push({ name: nextPage }).catch(() => {});
};

const getAutoLookUpRes = async () => {
  myCode.value = confirmationNumber.value;
  const info = {
    appointmentNumber: myCode.value.toUpperCase(),
    emailAddress: myEmail.value,
    phoneNumber: formatPhoneForRims(myPhone.value),
  };

  const reservationsFound = await store.dispatch(RESERVATION_FIND, info);

  if (reservationsFound.length > 0) {
    // Commit the data to the store before navigating.
    store.commit('user/setReservationsFound', reservationsFound);
    
    // Navigate without the invalid 'found' param.
    router.push({ name: PAGES.value.updateRes }).catch(() => {});
  }
};
</script>


<style scoped lang="scss">
.lookup {
  form {
    margin-top: 1.5em;
    .select-lookup {
      .radio-input {
        display: inline-block;
        vertical-align: middle;
      }
      label {
        font-weight: bold;
      }
    }
    .lookup-type{
      width: clamp(250px, 18vw, 22vw);
    }
    .or {
      @extend %font-sans-bold;
      font-size: 1.25rem;
      margin-right: 1.25em;
      padding-top: 1.85em;
    }
    .validateForm{
      margin-top: 0em !important;
    }
  }

  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
</style>
